import { ConfigInfoProps } from '../../configLookup/components/Props';
import React, { PropsWithChildren, useState } from 'react';
import {
    VegaAccordion,
    VegaBox,
    VegaDivider,
    VegaFlex,
    VegaFont,
} from '@globalpayments/vega-react';

interface Props {
    config: ConfigInfoProps;
}

interface ControlledAccordionProps extends PropsWithChildren {
    accordionId: string;
    title: string;
    drawsBackground?: boolean;
}

const ControlledAccordion: React.FC<ControlledAccordionProps> = ({
    accordionId,
    title,
    drawsBackground = false,
    children,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <VegaAccordion
            accordionTitle={title}
            drawsBackground={drawsBackground}
            expand={isExpanded}
            expandIconAlign="right"
            groupId={`config-info-${accordionId}`}
            onVegaExpand={(event) => setIsExpanded(event.detail)}
            showExpandIcon
        >
            <VegaBox padding={{ top: 'size-12' }}>{children}</VegaBox>
        </VegaAccordion>
    );
};

const Detail: React.FC<PropsWithChildren> = ({ children }) => (
    <VegaFont as="p" color="text-primary" variant="font-p2-short">
        {children}
    </VegaFont>
);

const ConfigAccordion: React.FC<Props> = ({ config }) => {
    return (
        <ControlledAccordion accordionId={`config-${config.id}`} title={config.configName} drawsBackground>
            <VegaFlex direction="col" gap="size-12">
                <Detail><strong>Channel:</strong> {config.channel}</Detail>
                <Detail><strong>Subchannel:</strong> {config.subChannel}</Detail>

                <ControlledAccordion accordionId={`config-${config.id}-leads`} title="Config Leads">
                    <VegaFlex direction="col" gap="size-8">
                        {config.configLeads.map((lead: any, index: number) => (
                            <ControlledAccordion
                                accordionId={`config-${config.id}-lead-${lead.id ?? 'unknown'}-${index}`}
                                key={`${lead.id ?? 'unknown'}-${index}`}
                                title={`Lead ${index}`}
                            >
                                <VegaFlex direction="col" gap="size-8">
                                    <VegaDivider />
                                    <Detail><strong>Creation Phase Name:</strong> {lead.creationPhaseName}</Detail>
                                    <Detail><strong>Lead Type:</strong> {lead.leadType}</Detail>
                                    <Detail><strong>Lead ID:</strong> {lead.id}</Detail>
                                </VegaFlex>
                            </ControlledAccordion>
                        ))}
                    </VegaFlex>
                </ControlledAccordion>

                <ControlledAccordion accordionId={`config-${config.id}-documents`} title="Config Documents">
                    <VegaFlex direction="col" gap="size-4">
                        {config.configDocuments.map((document: any, index: number) => (
                            <Detail key={`${document.documentTitle}-${document.fileName}-${index}`}>
                                &#x2022; {document.documentTitle} / {document.fileName}
                            </Detail>
                        ))}
                    </VegaFlex>
                </ControlledAccordion>

                <ControlledAccordion accordionId={`config-${config.id}-properties`} title="Config Properties">
                    <VegaFlex direction="col" gap="size-8">
                        {config.configProperties.map((property: any, index: number) => (
                            <ControlledAccordion
                                accordionId={`config-${config.id}-property-${property.id ?? 'unknown'}-${index}`}
                                key={`${property.id ?? 'unknown'}-${index}`}
                                title={`Property ${index}`}
                            >
                                <VegaFlex direction="col" gap="size-8">
                                    <VegaDivider />
                                    <Detail><strong>Property Key:</strong> {property.propertyKey}</Detail>
                                    <Detail><strong>Property Value:</strong> {property.propertyValue}</Detail>
                                    <Detail><strong>ID:</strong> {property.id}</Detail>
                                </VegaFlex>
                            </ControlledAccordion>
                        ))}
                    </VegaFlex>
                </ControlledAccordion>

                <ControlledAccordion accordionId={`config-${config.id}-phase-details`} title="Phase Details">
                    <VegaFlex direction="col" gap="size-8">
                        <VegaDivider />
                        <Detail><strong>Card Processing Rate:</strong> {config.entityProperties.CardProcessingRate}</Detail>
                        <Detail><strong>Per Transaction Charge:</strong> {config.entityProperties.CardProcessingPerTransactionCharge}</Detail>
                        <Detail><strong>Header Title:</strong> {config.entityProperties.HeaderTitle}</Detail>
                        <Detail><strong>Session Lifetime (Days):</strong> {config.entityProperties.SessionLifetimeInDays}</Detail>
                    </VegaFlex>
                </ControlledAccordion>
            </VegaFlex>
        </ControlledAccordion>
    );
};

export default ConfigAccordion;